<script setup lang="ts">
import { computed, inject } from 'vue';

export interface RadioProps {
  id: string;
  name: string;
  value: string | number | boolean;
  label: string;
  modelValue?: string | number | boolean;
  disabled?: boolean;
  hasError?: boolean;
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
}

const props = withDefaults(defineProps<RadioProps>(), {
  modelValue: undefined,
  disabled: false,
  hasError: false,
  wrapperClass: undefined,
  inputClass: undefined,
  labelClass: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean];
}>();

// Inject radio group context if available
const radioGroup = inject<{
  name: string;
  modelValue: { value: string | number | boolean | undefined };
  hasError: { value: boolean };
  errorId: { value: string | undefined };
  updateValue: (value: string | number | boolean) => void;
} | null>('radioGroup', null);

const effectiveName = computed(() => radioGroup?.name || props.name);
const effectiveModelValue = computed(() => radioGroup?.modelValue?.value ?? props.modelValue);
const effectiveHasError = computed(() => radioGroup?.hasError?.value ?? props.hasError);
const effectiveErrorId = computed(() => radioGroup?.errorId?.value);

const isChecked = computed(() => {
  return effectiveModelValue.value === props.value;
});

const inputClasses = computed(() => {
  const errorClass = effectiveHasError.value ? 'error' : '';
  return [errorClass, props.inputClass].filter(Boolean).join(' ');
});

const labelClasses = computed(() => {
  return props.labelClass || '';
});

const wrapperClasses = computed(() => {
  const baseClass = 'mb-5 radio-group';
  return [baseClass, props.wrapperClass].filter(Boolean).join(' ');
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.checked) {
    // If within a radio group, use the group's update function
    if (radioGroup) {
      radioGroup.updateValue(props.value);
    } else {
      emit('update:modelValue', props.value);
    }
  }
};
</script>

<template>
  <div :class="wrapperClasses">
    <input
      :id="id"
      :class="inputClasses"
      type="radio"
      :name="effectiveName"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      :aria-describedby="effectiveErrorId"
      @change="handleChange"
    />
    <label :for="id" :class="labelClasses">{{ label }}</label>
  </div>
</template>
